import * as Sentry from '@sentry/react';
import { consoleInfoLog, isProd } from 'App/Helpers';
import ReactGA from 'react-ga4';

import { captureCustomException } from '../../ErrorTracking/captureCustomException';

/**
 * Service for sending events to Google Analytics 4 via ReactGA.
 *
 * @class GoogleAnalytics
 */
export default class GoogleAnalytics {
    /**
     * Creates a new Google Analytics instance.
     *
     * @param {string} trackingId - GA4 Measurement ID.
     * @param {string} landingName - Label to attach to all events (e.g. page or campaign name).
     * @param {object} [gaOptions] - Optional gtag.js configuration options.
     * @param {object} [gtagOptions] - Optional additional gtag.js options.
     * @param {boolean} enabled - Whether analytics tracking is enabled.
     * @param {boolean} [immediateInit] - Indicates whether the analytics service should be initialized when the class is created.

     */
    constructor({
        trackingId,
        landingName,
        gaOptions = {},
        gtagOptions = {},
        enabled = true,
        immediateInit = true,
    }) {
        this.enabled = enabled;
        this.landingName = landingName;
        this.trackingId = trackingId;
        this.gaOptions = gaOptions;
        this.gtagOptions = gtagOptions;
        this.immediateInit = immediateInit;

        if (this.immediateInit) this.init();
    }

    /**
     * Returns the underlying ReactGA client.
     *
     * @returns {object} The ReactGA instance.
     */
    client() {
        return ReactGA;
    }

    /**
     * Initializes the ReactGA library.
     *
     * @returns {boolean | void}
     */
    init() {
        if (!this.enabled) return;

        try {
            ReactGA.initialize([
                {
                    trackingId: this.trackingId,
                    gaOptions: this.gaOptions,
                    gtagOptions: this.gtagOptions,
                },
            ]);
            consoleInfoLog('## Init: Google Analytics 4', 'init');

            return true;
        } catch (error) {
            if (isProd())
                captureCustomException(error, 'GoogleAnalyticsInitFailed');

            return false;
        }
    }

    /**
     * Sends a custom event to Google Analytics.
     *
     * @param {string} name - Event name (built-in or custom) as per gtag.js reference. https://developers.google.com/tag-platform/gtagjs/reference/events
     * @param {object} [params] - Optional parameters to include with the event.
     * @returns {void}
     */
    gaEvent(name, params) {
        if (!this.enabled) return;

        ReactGA.event(name, params);
    }

    /**
     * Sends a basic category/action/value event.
     *
     * @param {string} category - Event category (e.g. "game", "error", "push", "ad", "consent").
     * @param {string} [action] - Event action name.
     * @param {number} [value] - Optional numeric value to associate with the event.
     * @param {object} [options] - Optional fields for react-ga4 (e.g. `{ nonInteraction: true }`).
     * @returns {void}
     */
    basicGaEvent(category, action, value, options = {}) {
        if (!this.enabled) return;

        const normalizedAction = action
            .replace(/:/g, '_')
            .replace(/\s+/g, '')
            .toLowerCase();
        const eventOptions = {
            category,
            action: normalizedAction,
            label: this.landingName,
            ...(value !== undefined && { value }),
            ...(options.nonInteraction ? { nonInteraction: true } : {}),
        };

        ReactGA.event({ ...eventOptions });

        consoleInfoLog(
            `## Analytics: Google Event - ${normalizedAction}`,
            'ga'
        );
        const actionLength = normalizedAction.length;

        if (actionLength > 40) {
            const message = `[Google Analytics ${this.landingName}]: Event name is too long: ${normalizedAction}, length: ${actionLength}`;

            Sentry.withScope(function (scope) {
                scope.setLevel('error');
                scope.setTag('app', 'ga_events');
                Sentry.captureException(new Error(message));
            });
        }
    }
}
